import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { BankIDFormTextKeys } from '~/components/authentication/bankid/bankid-text-keys';
import Loading from '~/components/misc/loading';
import { QRCode } from 'react-qrcode-logo';
import QRCodeError from './qr-code-error';

import useCustomUI from '~/hooks/use-custom-ui';
import useTranslations from '~/components/translation/use-translations';

const StyledNumber = styled.span`
	border-radius: 50%;
	width: 30px;
	height: 30px;
	padding: 4px;
	background: var(--qrcode-instructions-number);
	color: var(--qrcode-instructions-text);
	text-align: center;
`;

const StyledInstruction = styled.div`
	span {
		font: var(--label-bold);
		text-transform: var(--brand-text-transform);
		margin-left: 6px;
	}
	display: flex;
	align-items: center;
	width: calc(85% - 0.5rem);
	padding-bottom: 16px;
`;

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const QRCodeContainer = styled.div`
	display: flex;
	justify-content: center;
	padding: 16px 0;
`;

const qrCodeColors = {
	qrOuterCornerColor: '--qrcode-image-outer-corner',
	qrInnerCornerColor: '--qrcode-image-inner-corner',
	qrBackgroundColor: '--qrcode-image-background',
	qrForegroundColor: '--qrcode-image-foreground'
};

export default function BankIdQRCode({ transaction, gaEventCb, onSubmit }) {
	const { translations } = useTranslations(BankIDFormTextKeys);
	const { status, response } = transaction ?? {};

	const [refresh, setRefresh] = useState(false);
	const qrCodeUI = useCustomUI(qrCodeColors, customUI => ({
		eyeColor: [
			{ outer: customUI.qrOuterCornerColor, inner: customUI.qrInnerCornerColor }, // top left corner
			{ outer: customUI.qrOuterCornerColor, inner: customUI.qrInnerCornerColor }, // top right corner
			{ outer: customUI.qrOuterCornerColor, inner: customUI.qrInnerCornerColor } // bottom left corner
		],
		bgColor: customUI.qrBackgroundColor,
		fgColor: customUI.qrForegroundColor
	}));

	const instructions = useMemo(
		() => [
			{ number: 1, text: translations.INSTRUCTION_START_BANKID_APP },
			{ number: 2, text: translations.INSTRUCTION_CLICK_SCAN },
			{ number: 3, text: translations.INSTRUCTION_SCAN_QRCODE }
		],
		[
			translations.INSTRUCTION_CLICK_SCAN,
			translations.INSTRUCTION_SCAN_QRCODE,
			translations.INSTRUCTION_START_BANKID_APP
		]
	);
	const handleTryAgain = () => {
		setRefresh(prevRefresh => !prevRefresh);
	};

	useEffect(() => {
		onSubmit({ qrMode: true });

		gaEventCb({
			step: 'qr-code-screen'
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refresh]);

	const errors = {
		failure: translations.QRCODE_ERROR_FAILED,
		server_error: translations.QRCODE_ERROR_SERVER
	};

	if (status in errors) {
		return (
			<QRCodeError
				mainTitle={translations.QRCODE_ERROR_TITLE}
				subTitle={errors[status]}
				buttonText={translations.MOBILE_RESULT_FAIL_TRY_AGAIN_BUTTON}
				onTryAgain={handleTryAgain}
			/>
		);
	}

	if (!response?.qrData) {
		return <Loading />;
	}

	return (
		<>
			<QRCodeContainer>
				<QRCode
					size={180}
					eyeRadius={0}
					value={response?.qrData}
					ecLevel="M"
					eyeColor={qrCodeUI.eyeColor}
					bgColor={qrCodeUI.bgColor}
					fgColor={qrCodeUI.fgColor}
				/>
			</QRCodeContainer>
			<StyledContainer>
				{instructions.map(({ number, text }) => (
					<StyledInstruction key={number}>
						<StyledNumber>{number}</StyledNumber>
						<span>{text}</span>
					</StyledInstruction>
				))}
			</StyledContainer>
		</>
	);
}
